/* eslint i18next/no-literal-string: 0 */
import React from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { RootState } from '../../store/rootReducer'
import { BackButtonType } from '../../store/common/commonSliceTyping'
import { switchMenu, fixAppContainer, pageLoaded, deleteLastUrl } from '../../store/common/commonSlice'
import { createLocalizedPath } from '../../utils/localizationHelpers'
import paths from '../../paths'
import last from 'lodash/last'
import { useTranslation } from 'react-i18next'

import './MenuMobile.sass'
import { IconArrowNarrowLeft, IconClock24, IconHome, IconMail, IconMenuDeep, IconMessages } from '@tabler/icons-react'

const useScrollDirection = () => {
    const [hidden, setHidden] = React.useState(false)
    const lastScrollTop = React.useRef(0)
    const initialized = React.useRef(false)
    const isSliding = React.useRef(false)
    const location = useLocation()

    React.useEffect(() => {
        initialized.current = false
        lastScrollTop.current = 0
        if (!isSliding.current) {
            setHidden(false)
        }
    }, [location.pathname])

    React.useEffect(() => {
        let slidingTimeout: ReturnType<typeof setTimeout>

        const onSliding = () => {
            isSliding.current = true
            clearTimeout(slidingTimeout)
            slidingTimeout = setTimeout(() => {
                isSliding.current = false
            }, 1000)
        }

        const onSlidingEnd = () => {
            clearTimeout(slidingTimeout)
            isSliding.current = false
        }

        window.addEventListener('swiperSliding', onSliding)
        window.addEventListener('swiperSlidingEnd', onSlidingEnd)
        return () => {
            window.removeEventListener('swiperSliding', onSliding)
            window.removeEventListener('swiperSlidingEnd', onSlidingEnd)
            clearTimeout(slidingTimeout)
        }
    }, [])

    React.useEffect(() => {
        const handler = (e: CustomEvent) => {
            if (isSliding.current) return

            const { scrollTop } = e.detail

            if (!initialized.current) {
                initialized.current = true
                if (scrollTop > 128) {
                    setHidden(true)
                }
                lastScrollTop.current = scrollTop
                return
            }

            const delta = scrollTop - lastScrollTop.current
            if (Math.abs(delta) > 4) {
                if (delta > 0 && scrollTop > 128) {
                    setHidden(true)
                } else if (delta < 0) {
                    setHidden(false)
                }
                lastScrollTop.current = scrollTop
            }
        }

        window.addEventListener('appScroll', handler as EventListener)
        return () => window.removeEventListener('appScroll', handler as EventListener)
    }, [])

    return hidden
}

type NavItem = {
    id: string
    label: string
    icon: React.ReactNode
    action: () => void
    badge?: number
    requiresAuth?: boolean
}

class FullPmState {}

const FORUM_URL = '/forum/'

const MenuMobile = () => {
    const [t] = useTranslation()

    const isHidden = useScrollDirection()

    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()

    const loggedIn = useSelector<RootState, boolean>((state) => state.member.loggedIn)
    const backButton = useSelector<RootState, BackButtonType>((state) => state.common.backButton)
    const pm = useSelector<RootState, FullPmState>((state) => state.pm)

    // @ts-ignore
    const messagesCounters = pm ? pm.counters.requested_by_recipient + pm.counters.allowed_unread : 0

    const goBack = () => {
        if (backButton && backButton.urlList.length === 0) {
            history.push(createLocalizedPath(paths.index.path))
            return
        }
        if (last(backButton.urlList) === location.pathname) {
            if (backButton.urlList.length === 1 || !backButton.urlList[backButton.urlList.length - 2]) {
                history.push(createLocalizedPath(paths.index.path))
                return
            }
            history.push(backButton.urlList[backButton.urlList.length - 2])
            dispatch(deleteLastUrl())
        } else {
            history.push(last(backButton.urlList))
        }
        dispatch(deleteLastUrl())
    }

    const firstItem: NavItem = backButton.isNecessaryToShowBackButton
        ? {
              id: 'back',
              label: 'Retour',
              icon: backButton.avatarUrl ? (
                  <img src={backButton.avatarUrl} alt="" className="mobileBottomNav__avatar" />
              ) : (
                  <IconArrowNarrowLeft size={24} />
              ),
              action: goBack
          }
        : {
              id: 'home',
              label: 'Home',
              icon: <IconHome size={24} />,
              action: () => window.location.assign(createLocalizedPath(paths.index.path))
          }

    const allItems: NavItem[] = [
        firstItem,
        {
            id: 'ephemeral',
            label: 'Stories',
            icon: <IconClock24 size={24} />,
            action: () => history.push(createLocalizedPath(paths.temporaryPhotos.path ?? '/'))
        },
        {
            id: 'messages',
            label: 'Messages',
            icon: <IconMail size={24} />,
            badge: messagesCounters,
            requiresAuth: true,
            action: () => history.push(createLocalizedPath(paths.memberMessagePmConversations.path ?? '/'))
        },
        {
            id: 'forum',
            label: 'Forum',
            icon: <IconMessages size={24} />,
            action: () => {
                window.location.href = FORUM_URL
            }
        },
        {
            id: 'menu',
            label: 'Menu',
            icon: <IconMenuDeep size={24} />,
            action: () => {
                dispatch(switchMenu())
                dispatch(pageLoaded())
                dispatch(fixAppContainer())
            }
        }
    ]

    const navItems = allItems.filter((item) => !(item.requiresAuth && !loggedIn))

    const isActive = (item: NavItem): boolean => {
        if (item.id === 'back' || item.id === 'menu' || item.id === 'forum') return false

        const pathMap: Record<string, string> = {
            home: paths.index.path,
            ephemeral: paths.temporaryPhotos.path ?? '',
            messages: paths.memberMessagePmConversations.path ?? ''
        }

        const mapped = pathMap[item.id]
        if (!mapped) return false

        if (item.id === 'home') return location.pathname === createLocalizedPath(paths.index.path)
        return location.pathname.includes(mapped)
    }

    return (
        <nav
            className="mobileBottomNav"
            role="navigation"
            aria-label="Mobile navigation"
            style={{
                transform: isHidden ? 'translateY(100%)' : 'translateY(0)',
                opacity: isHidden ? '0' : '1',
                transition: 'all 0.6s ease',
                transitionDelay: '200ms'
            }}
        >
            {navItems.map((item) => (
                <button
                    key={item.id}
                    className={[
                        'mobileBottomNav__item',
                        isActive(item) ? 'mobileBottomNav__item--active' : '',
                        item.id === 'back' ? 'mobileBottomNav__item--back' : '',
                        item.id === 'menu' ? 'mobileBottomNav__item--burger' : ''
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={item.action}
                    aria-label={item.label}
                    data-testid={`mobileNav_${item.id}`}
                >
                    <span className="mobileBottomNav__icon">
                        {item.icon}
                        {item.badge != null && item.badge > 0 && (
                            <span className="mobileBottomNav__badge">{item.badge > 99 ? '99+' : item.badge}</span>
                        )}
                    </span>
                    <span className="mobileBottomNav__label">{t(item.label)}</span>
                </button>
            ))}
        </nav>
    )
}

export default MenuMobile
